import React from 'react';
import { Table, TablePaginationConfig } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { TTBFraudStatus, TTBFraudTableDataType, TTBFraudType } from '../../../types/tryThenBuyFraud';
import { TRY_THEN_BUY_FRAUD } from '../../../constants/define/translation';
import {
  ActionsColumn,
  CommonColumn,
  DateColumn,
  EmailColumn,
  IndexColumn,
  ProductNameColumn,
  StatusColumn,
} from './columns';
import { getDetectionHighlightTTBFraud } from '../../../utils/transfer/ttb-fraud';
import { appStyles } from '../../../styles';

interface TTBFraudTableProps {
  records: TTBFraudTableDataType[];
  loading?: boolean;
  className?: string;
  onRefresh: () => void;
  onChangeSort: (sorter: any) => any;
  pagination?: TablePaginationConfig;
  isSearching?: boolean;
}

const { Column } = Table;
export const TTBFraudTable = (props: TTBFraudTableProps) => {
  const { t } = useTranslation(TRY_THEN_BUY_FRAUD);
  const googleMap = 'https://www.google.com/maps/place';
  const { onRefresh, records, pagination, onChangeSort, isSearching } = props;

  const handleTableChange = (a: any, b: any, sorter: any) => {
    let sortType = null;
    if (sorter.order === 'descend') {
      sortType = 'desc';
    }
    if (sorter.order === 'ascend') {
      sortType = 'asc';
    }
    return onChangeSort({ sortBy: sortType ? sorter.field : null, sortType });
  };

  return (
    <>
      <StyledTable
        dataSource={records}
        loading={props.loading}
        className={props.className}
        rowKey="index"
        pagination={pagination}
        scroll={{ x: 1440 }}
        locale={{ emptyText: isSearching ? t('message.orderNotFound') : undefined }}
        showSorterTooltip={false}
        sortDirections={['descend', 'ascend']}
        onChange={handleTableChange}
      >
        <Column
          title={t('table.title.index')}
          align="center"
          width={32}
          dataIndex="index"
          key="index"
          render={(value, record: TTBFraudTableDataType) => (
            <IndexColumn value={value} className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`} />
          )}
        />
        <Column
          title={t('table.title.orderId')}
          align="left"
          width={200}
          dataIndex="orderId"
          key="orderId"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <CommonColumn
              value={value}
              minWidth={100}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
        />
        <Column
          title={t('table.title.email')}
          align="left"
          width={200}
          dataIndex="email"
          key="email"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <EmailColumn
              email={value}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
              width={200}
            />
          )}
        />
        <Column
          title={t('table.title.firstName')}
          align="left"
          width={200}
          dataIndex="firstName"
          key="firstName"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <CommonColumn
              value={value}
              minWidth={80}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
        />
        <Column
          title={t('table.title.lastName')}
          align="left"
          width={200}
          dataIndex="lastName"
          key="lastName"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <CommonColumn
              value={value}
              minWidth={80}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
        />
        <Column
          title={t('table.title.accountCreationDate')}
          align="center"
          width={200}
          dataIndex="userCreatedAt"
          key="accountCreationDate"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <DateColumn
              date={value}
              minWidth={100}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
        />
        <Column
          title={t('table.title.orderTime')}
          align="center"
          width={200}
          dataIndex="ttbCreatedAt"
          key="orderTime"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <DateColumn
              date={record?.ttbCreatedAt || record.createdAt}
              minWidth={100}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
        />
        <Column
          title={t('table.title.orderedBySimulator')}
          align="center"
          width={200}
          dataIndex="isSimulator"
          key="orderedBySimulator"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <div className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}>
              {value ? t('table.body.yes') : t('table.body.no')}
            </div>
          )}
        />
        <Column
          title={t('table.title.ttbFingerprint')}
          align="center"
          width={130}
          dataIndex="fingerPrint"
          key="ttbFingerprint"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <CommonColumn
              value={value}
              width={200}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
        />
        <Column
          title={t('table.title.signUpIPAddress')}
          align="center"
          width={200}
          dataIndex="signUpIPAddress"
          key="signUpIPAddress"
          render={(value, record: TTBFraudTableDataType) => (
            <div className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}>
              {record.signUpIPAddress}
              <div className="location">{record.signUpIPAddressLocation}</div>
            </div>
          )}
        />
        <Column
          title={t('table.title.orderIPAddress')}
          align="center"
          width={200}
          dataIndex="purchaseIPAddress"
          key="orderIPAddress"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <div className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}>
              {record.purchaseIPAddress}
              <div className="location">{record.purchaseIPAddressLocation}</div>
            </div>
          )}
        />
        <Column
          title={t('table.title.signUpGPS')}
          align="center"
          width={130}
          dataIndex="signUpGPS"
          key="signUpGPS"
          render={(value, record: TTBFraudTableDataType) => (
            <a
              href={`${googleMap}/${record.signUpLatitude}, ${record.signUpLongitude}`}
              target="_blank"
              rel="noreferrer"
            >
              <div className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}>
                {!!record.signUpLatitude && record.signUpLatitude}
                {!!record.signUpLatitude && !!record.signUpLongitude && ', '}
                {!!record.signUpLongitude && record.signUpLongitude}
                <div className="location">{record.signUpGPSLocation}</div>
              </div>
            </a>
          )}
        />
        <Column
          title={t('table.title.orderGPS')}
          align="center"
          width={200}
          dataIndex="purchaseLatitude"
          key="orderGPS"
          sorter
          render={(value, record: TTBFraudTableDataType) => (
            <a
              href={`${googleMap}/${record.purchaseLatitude}, ${record.purchaseLongitude}`}
              target="_blank"
              rel="noreferrer"
            >
              <div className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}>
                {!!record.purchaseLatitude && record.purchaseLatitude}
                {!!record.purchaseLatitude && !!record.purchaseLongitude && ', '}
                {!!record.purchaseLongitude && record.purchaseLongitude}
                <div className="location">{record.purchaseGPSLocation}</div>
              </div>
            </a>
          )}
        />
        <Column
          title={t('table.title.potentialFraud')}
          align="center"
          width={130}
          dataIndex="potentialFraud"
          key="potentialFraud"
          render={(value, record: TTBFraudTableDataType) => (
            <CommonColumn
              value={`${value}%`}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
        />
        <Column
          title={t('table.title.reason')}
          align="left"
          width={130}
          dataIndex="duplicate"
          key="reason"
          render={(value, record: TTBFraudTableDataType) => (
            <CommonColumn
              value={value}
              minWidth={150}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
        />
        <Column
          title={t('table.title.gatStatus')}
          align="center"
          width={150}
          dataIndex="gatStatus"
          key="gatStatus"
          render={(value, record: any) => (
            <CommonColumn
              value={value}
              minWidth={100}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
        />
        <Column
          title={t('table.title.status')}
          align="center"
          width={80}
          dataIndex="status"
          key="status"
          render={(_, record: any) => (
            <StatusColumn
              status={record.status}
              isActive={record.status && [TTBFraudStatus.BOUGHT, TTBFraudStatus.CHARGED].includes(record.status)}
              width="160px"
              record={record}
            />
          )}
        />
        <Column
          title={t('table.title.productName')}
          align="left"
          dataIndex="productName"
          key="productName"
          render={(value, record: TTBFraudTableDataType) => (
            <ProductNameColumn
              record={record}
              className={`isHighLightTTBFraud-${getDetectionHighlightTTBFraud(record)}`}
            />
          )}
          width={150}
        />
        <Column
          title={t('table.title.action')}
          align="center"
          key="action"
          width={60}
          render={(_, record: TTBFraudType) => <ActionsColumn onRefresh={onRefresh} record={record} />}
        />
      </StyledTable>
    </>
  );
};

const StyledTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }

    .ant-table-column-sorter {
      margin-left: 8px;
    }
  }

  .ant-pagination-item a {
    line-height: 30px;
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }

  & .isHighLightTTBFraud-red {
    ${appStyles.color.red}
    & span {
      ${appStyles.color.red}
    }
  }

  & .isHighLightTTBFraud-orange {
    ${appStyles.color.orange}
    & span {
      ${appStyles.color.orange}
    }
  }

  & .isHighLightTTBFraud-green {
    ${appStyles.color.green}
    & span {
      ${appStyles.color.green}
    }
  }

  & .isHighLightTTBFraud-original {
    ${appStyles.color.black}
  }

  & .location {
    font-size: 11px;
    width: 150px;
  }
`;
